import React from 'react';
import { CodeOutlined } from '@ant-design/icons';
import type { PiletApi } from '@sharefiledev/sharefile-appshell';
import {
	permissionsPiletExtensionSlots,
	permissionsPiletModals,
} from '@sharefiledev/sharing-pilet-sdk';
import {
	AsyncAssignButton,
	AsyncAvatarGroup,
	AsyncAvatarGroupWithShareButton,
	AsyncGetAuthorizedUsers,
	AsyncManagePrincipalsContent,
	AsyncShareButton,
	LazyDemoPage,
} from './ExtensionSlots';
import { LazyGrantPage, LazyLinkErrorPage, LazyLinksPage } from './Links';
import { setLogger } from './logger';
import {
	AsyncLinkBasedManageUsersModalContent,
	AsyncLinkBasedShareModal,
	AsyncManageUsersModalContent,
	AsyncShareModalContent,
} from './Modals';
import {
	getDefaultLinkBasedShareModalOptions,
	getDefaultLinkManageUsersModalOptions,
	getDefaultManageUsersModalOptions,
	getDefaultShareModalOptions,
} from './Modals/getDefaultModalOptions';
import {
	GRANT_ACCESS_PATH,
	LINK_ERROR_PATH,
	PUBLIC_LINK_PATH,
	RESOLVE_ACCESS_PATH,
} from './utils/constants';

export function setup(piletApi: PiletApi) {
	setLogger(piletApi.sf.getLogger());

	piletApi.registerExtension(
		permissionsPiletExtensionSlots.shareButtonSlot,
		({ params }) => <AsyncShareButton piletApi={piletApi} {...params} />
	);

	piletApi.registerExtension(permissionsPiletExtensionSlots.avatarGroupSlot, props => (
		<React.Suspense fallback={<div data-testid="loading-avatar-group" />}>
			<AsyncAvatarGroup {...props} />
		</React.Suspense>
	));

	piletApi.registerExtension(
		permissionsPiletExtensionSlots.avatarGroupWithShareButtonSlot,
		({ params }) => <AsyncAvatarGroupWithShareButton piletApi={piletApi} {...params} />
	);
	piletApi.registerExtension(
		permissionsPiletExtensionSlots.assignButtonSlot,
		({ params }) => <AsyncAssignButton piletApi={piletApi} {...params} />
	);

	piletApi.registerExtension(
		permissionsPiletExtensionSlots.managePrincipalsContentSlot,
		({ params }) => <AsyncManagePrincipalsContent piletApi={piletApi} {...params} />
	);

	piletApi.registerExtension(
		permissionsPiletExtensionSlots.getAuthorizedUsersSlot,
		({ params }) => <AsyncGetAuthorizedUsers piletApi={piletApi} {...params} />
	);

	piletApi.registerModal(
		permissionsPiletModals.shareModal,
		({ options, cancelInteraction, completeInteraction }) => (
			<AsyncShareModalContent
				piletApi={piletApi}
				completeInteraction={completeInteraction}
				cancelInteraction={cancelInteraction}
				{...options!}
			/>
		),
		getDefaultShareModalOptions()
	);

	piletApi.registerModal(
		permissionsPiletModals.linkBasedShareModal,
		({ options, cancelInteraction, completeInteraction }) => (
			<AsyncLinkBasedShareModal
				piletApi={piletApi}
				completeInteraction={completeInteraction}
				cancelInteraction={cancelInteraction}
				{...options!}
			/>
		),
		getDefaultLinkBasedShareModalOptions()
	);

	piletApi.registerModal(
		permissionsPiletModals.manageUsersModal,
		({ options, cancelInteraction, completeInteraction }) => (
			<AsyncManageUsersModalContent
				piletApi={piletApi}
				completeInteraction={completeInteraction}
				cancelInteraction={cancelInteraction}
				{...options!}
			/>
		),
		getDefaultManageUsersModalOptions()
	);

	piletApi.registerModal(
		permissionsPiletModals.linkBasedManageUserModal,
		({ options, cancelInteraction, completeInteraction }) => (
			<AsyncLinkBasedManageUsersModalContent
				piletApi={piletApi}
				completeInteraction={completeInteraction}
				cancelInteraction={cancelInteraction}
				{...options!}
			/>
		),
		getDefaultLinkManageUsersModalOptions()
	);

	piletApi.registerPage(
		`${RESOLVE_ACCESS_PATH}/:lookupkey`,
		() => <LazyLinksPage piletApi={piletApi} />,
		{ pageType: 'fullPage' }
	);

	piletApi.registerPage(
		`${GRANT_ACCESS_PATH}/:linkKey`,
		() => <LazyGrantPage piletApi={piletApi} />,
		{ pageType: 'fullPage' }
	);

	piletApi.registerPage(
		`${LINK_ERROR_PATH}`,
		() => <LazyLinkErrorPage piletApi={piletApi} isBackToSignInButtonEnabled={false} />,
		{ pageType: 'unauthenticated' }
	);

	piletApi.registerPage(
		`${PUBLIC_LINK_PATH}/:lookupkey`,
		() => <LazyLinksPage piletApi={piletApi} isPublicLink />,
		{ pageType: 'unauthenticated' }
	);

	registerDevPage(piletApi);
}

function registerDevPage(piletApi: PiletApi) {
	if (process.env.NODE_ENV === 'development') {
		piletApi.sf.registerLeftNavComponent({
			href: '/permissions-dev',
			title: () => 'Permissions Dev',
			icon: () => <CodeOutlined style={{ fontSize: '24px' }} />,
			weight: 50,
		});
		if (typeof piletApi.meta.config.apiOrigin === 'string') {
			const { configureHttp } = require('./PermissionHttpConfiguration');
			const { Configuration } = require('@sharefiledev/sharing-pilet-sdk/api');
			configureHttp((configuration: any) => {
				configuration.config = new Configuration({
					basePath: `${piletApi.meta.config.apiOrigin}/io/permissions/v1`,
				});
			});
		}
	}

	piletApi.registerPage('/permissions-dev', () => <LazyDemoPage piletApi={piletApi} />);
}
